import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { CommonPrintService, worksheetPrintService } from 'src/service/print'

import _Button from '@/component/atoms/Button/_Button/_Button'
import { default as Checkbox } from '@/component/atoms/Checkbox/_Checkbox'
import { default as CheckboxButton } from '@/component/atoms/CheckboxButton'
import { Dropdown, DropdownItem } from '@/component/molecules/Dropdown'
import { default as PlusMinusInput } from '@/component/molecules/PlusMinusInput'
import { Radio } from '@/component/molecules/Radio'

import S from './Print.style'

type Props = {
  printTargetIds: number[]
  afterPrintFn: VoidFunction
  title?: string
}

export const ArithmeticPrintModal = observer(
  ({ printTargetIds, afterPrintFn, title = '연산' }: Props) => {
    const handlePrintClick = async () => {
      await worksheetPrintService.onPrint(printTargetIds, 'ARITHMETIC', {
        order: 'ignore-order',
      })
      afterPrintFn()
    }

    const initialSync = async () => {
      worksheetPrintService.fetchInitPrintList(printTargetIds, [])

      worksheetPrintService.uiStore.update({
        visibleStudentOption: 'NONE',
      })
      worksheetPrintService.rangeOption.update({
        answer: false,
        omr: false,
      })
    }

    const isPrintEnabled = () => {
      if (worksheetPrintService.uiStore.visibleRangeOption === 'SELECT') {
        return (
          worksheetPrintService.rangeOption.problem || worksheetPrintService.rangeOption.quickAnswer
        )
      }
      return true
    }

    useEffect(() => {
      initialSync()
    }, [])

    return (
      <section css={[S.Wrap, S.Calculate]}>
        <header>
          <h3>{title} 인쇄</h3>
        </header>
        <div className="print-body">
          <div className="print-row print-count">
            <p className="row-left">매수</p>
            <div className="row-right">
              <PlusMinusInput
                value={worksheetPrintService.printOption.copyNumber}
                max={CommonPrintService.MAX_PRINT_QUANTITY}
                min={1}
                onChange={(copyNumber) => {
                  worksheetPrintService.printOption.update({ copyNumber })
                }}
                editable
              />
            </div>
          </div>

          <div className="print-row print-range">
            <p className="row-left">범위</p>
            <div className="row-right">
              <Radio.Group
                className="range-radio"
                value={worksheetPrintService.uiStore.visibleRangeOption}
                onChange={(event) =>
                  worksheetPrintService.uiStore.update({
                    visibleRangeOption: event.target.value as 'ALL' | 'SELECT',
                  })
                }
              >
                <Radio value="ALL">전체</Radio>
                <Radio value="SELECT">페이지 선택</Radio>
              </Radio.Group>

              {worksheetPrintService.uiStore.visibleRangeOption === 'SELECT' && (
                <div className="range-option-area">
                  <CheckboxButton
                    checked={worksheetPrintService.rangeOption.problem}
                    onClick={() =>
                      worksheetPrintService.onChangeRangeOption({
                        problem: !worksheetPrintService.rangeOption.problem,
                      })
                    }
                  >
                    문제지
                  </CheckboxButton>
                  <CheckboxButton
                    checked={worksheetPrintService.rangeOption.quickAnswer}
                    onClick={() =>
                      worksheetPrintService.onChangeRangeOption({
                        quickAnswer: !worksheetPrintService.rangeOption.quickAnswer,
                      })
                    }
                  >
                    빠른정답
                  </CheckboxButton>
                </div>
              )}
            </div>
          </div>

          {worksheetPrintService.printAgent && (
            <div className="print-row printer-select">
              <p className="row-left">프린터 선택</p>
              <div className="row-right">
                <div className="print-dropdown">
                  <Dropdown
                    height={40}
                    width={344}
                    listHeight={120}
                    value={`${worksheetPrintService.printAgent.selectedPrinter?.machineId}:${worksheetPrintService.printAgent?.selectedPrinter?.displayName}`}
                    onChange={(value: string) => {
                      const [mid, printName] = value.split(':')
                      const printer = worksheetPrintService.printAgent?.printerList.find(
                        ({ displayName, machineId }) =>
                          displayName === printName && machineId === mid,
                      )

                      worksheetPrintService.selectPrinter(printer!)
                    }}
                  >
                    {worksheetPrintService.printAgent.printerList.map((printer) => (
                      <DropdownItem
                        key={`${printer.machineId}:${printer.displayName}`}
                        data-mathflat-unique-id={`${printer.machineId}:${printer.displayName}`}
                        value={`${printer.machineId}:${printer.displayName}`}
                      >
                        {printer.displayName}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                </div>
              </div>
            </div>
          )}

          <div className="print-row print-option">
            <p className="row-left">옵션</p>
            <div className="row-right">
              <Checkbox
                onChange={(isDuplex) => worksheetPrintService.printOption.update({ isDuplex })}
                checked={worksheetPrintService.printOption.isDuplex}
                text={<span>양면</span>}
              />
              <Checkbox
                onChange={(isMonochrome) =>
                  worksheetPrintService.printOption.update({ isMonochrome })
                }
                checked={worksheetPrintService.printOption.isMonochrome}
                text={<span>흑백</span>}
              />
              <Checkbox
                onChange={(isReverse) => worksheetPrintService.printOption.update({ isReverse })}
                checked={worksheetPrintService.printOption.isReverse}
                text={<span>역순</span>}
              />
            </div>
          </div>
        </div>

        <div className="print-footer">
          <_Button
            minWidth={144}
            size="m"
            disabled={!isPrintEnabled()}
            theme="primary"
            onClick={handlePrintClick}
          >
            인쇄하기
          </_Button>
        </div>
      </section>
    )
  },
)
